import raf from "raf";

/**
 * Used on any method to make sure that it'll never be executed more than once
 * per animation frame.
 */
export const throttleToAnimationFrame = () => {
  return (target: {}, propertyKey: string, descriptor: PropertyDescriptor) => {
    // Create a unique symbol, which we're going to use when determining if
    // a callback is already pending
    const pendingSymbol = Symbol(
      "@use/decorators.throttleToAnimationFrame.pending"
    );

    return {
      // Forward configurable and enumerable "as is"
      configurable: descriptor.configurable,
      enumerable: descriptor.enumerable,

      // Overrite the original callback, so that we can make sure it'll
      // only be triggered once
      value(...args: unknown[]): any {
        // tslint:disable-line no-any
        // Create a strongly typed version of the current execution
        // context, so that we can apply / extract cache from it
        const context = <
          unknown & {
            [pendingSymbol]?: boolean;
          }
        >this; // tslint:disable-line no-invalid-this

        // If execution is already pending, then prevent further
        // executions in the same animation frame
        if (context[pendingSymbol]) {
          return;
        }

        context[pendingSymbol] = true;

        // Defer execution until next available animation frame
        raf(() => {
          // tslint:disable-next-line no-unsafe-any
          (<(...args: unknown[]) => unknown>descriptor.value).call(
            context,
            ...args
          );

          // Reset state to allow execution again upon next available
          // animation frame.
          context[pendingSymbol] = false;
        });
      },
    };
  };
};
